import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PlusIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  AcademicCapIcon,
  SwatchIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import RatingScaleForm from "../components/skills/RatingScaleForm";
import SkillCategoryForm from "../components/skills/SkillCategoryForm";
import SkillForm from "../components/skills/SkillForm";
import { apiService } from "../services/api";

const SkillsManagement = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("categories");
  const [loading, setLoading] = useState(true);

  // Data states
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [ratingScales, setRatingScales] = useState([]);

  // Modal states
  const [showRatingScaleModal, setShowRatingScaleModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showCategoryDetailsModal, setShowCategoryDetailsModal] =
    useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteContext, setDeleteContext] = useState({ type: "", item: null });
  const [deleting, setDeleting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const loadAllData = useCallback(async () => {
    try {
      const [categoriesData, skillsData, scalesData] = await Promise.all([
        apiService.getClubSkillCategories(clubId),
        apiService.getClubSkills(clubId),
        apiService.getClubRatingScales(clubId),
      ]);
      setCategories(categoriesData);
      setSkills(skillsData);
      setRatingScales(scalesData);
    } catch (error) {
      console.error("Error loading skills data:", error);
      toast.error("Failed to load skills data");
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleCreateRatingScale = () => {
    setEditingItem(null);
    setShowRatingScaleModal(true);
  };

  const handleEditRatingScale = (scale) => {
    setEditingItem(scale);
    setShowRatingScaleModal(true);
  };

  const handleDeleteRatingScale = async () => {
    if (!deleteContext.item) return;

    setDeleting(true);
    try {
      await apiService.deleteRatingScale(deleteContext.item.id);
      await loadAllData();
      toast.success("Rating scale deleted successfully");
      setShowDeleteModal(false);
      setDeleteContext({ type: "", item: null });
    } catch (error) {
      toast.error("Failed to delete rating scale");
    }
    setDeleting(false);
  };

  const handleDeleteRatingScaleClick = (scale) => {
    setDeleteContext({
      type: "rating_scale",
      item: scale,
      title: "Delete Rating Scale",
      message: `Are you sure you want to delete "${scale.name}"? This will affect all skills using this rating scale.`,
    });
    setShowDeleteModal(true);
  };

  const handleCreateCategory = () => {
    setEditingItem(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingItem(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async () => {
    if (!deleteContext.item) return;

    setDeleting(true);
    try {
      await apiService.deleteSkillCategory(deleteContext.item.id);
      await loadAllData();
      toast.success("Category deleted successfully");
      setShowDeleteModal(false);
      setDeleteContext({ type: "", item: null });
    } catch (error) {
      toast.error("Failed to delete category");
    }
    setDeleting(false);
  };

  const handleDeleteCategoryClick = (category) => {
    setDeleteContext({
      type: "category",
      item: category,
      title: "Delete Category",
      message: `Are you sure you want to delete "${category.name}"? This will delete all skills in this category.`,
    });
    setShowDeleteModal(true);
  };

  const handleCreateSkill = (categoryId = null) => {
    setEditingItem({ categoryId });
    setShowSkillModal(true);
  };

  const handleEditSkill = (skill) => {
    setEditingItem(skill);
    setShowSkillModal(true);
  };

  const handleDeleteSkill = async () => {
    if (!deleteContext.item) return;

    setDeleting(true);
    try {
      await apiService.deleteSkill(deleteContext.item.id);
      await loadAllData();
      toast.success("Skill deleted successfully");
      setShowDeleteModal(false);
      setDeleteContext({ type: "", item: null });
    } catch (error) {
      toast.error("Failed to delete skill");
    }
    setDeleting(false);
  };

  const handleDeleteSkillClick = (skill) => {
    setDeleteContext({
      type: "skill",
      item: skill,
      title: "Delete Skill",
      message: `Are you sure you want to delete "${skill.name}"?`,
    });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    switch (deleteContext.type) {
      case "rating_scale":
        await handleDeleteRatingScale();
        break;
      case "category":
        await handleDeleteCategory();
        break;
      case "skill":
        await handleDeleteSkill();
        break;
      default:
        toast.error("Unknown delete context");
    }
  };

  const handleViewCategoryDetails = (category) => {
    setSelectedCategory(category);
    setShowCategoryDetailsModal(true);
  };

  const getTabContent = () => {
    switch (activeTab) {
      case "scales":
        return <RatingScalesTab />;
      case "skills":
        return <SkillsTab />;
      default:
        return <CategoriesTab />;
    }
  };

  const CategoriesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Skill Categories ({categories.length})
        </h2>
        <Button onClick={handleCreateCategory}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <SwatchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No categories yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your first skill category to organize your evaluation
              criteria.
            </p>
            <div className="mt-6">
              <Button onClick={handleCreateCategory}>
                Create First Category
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Card
              key={category.id}
              className="hover:shadow-md transition-shadow"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="gray">
                      {
                        skills.filter((s) => s.category_id === category.id)
                          .length
                      }{" "}
                      skills
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewCategoryDetails(category)}
                      className="p-1"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {category.description && (
                  <p className="text-sm text-gray-600 mb-4">
                    {category.description}
                  </p>
                )}

                <div className="space-y-2">
                  {skills
                    .filter((skill) => skill.category_id === category.id)
                    .slice(0, 3)
                    .map((skill) => (
                      <div key={skill.id} className="text-sm">
                        <span className="font-medium">{skill.name}</span>
                        <span className="text-gray-500 ml-2">
                          (
                          {
                            ratingScales.find(
                              (s) => s.id === skill.rating_scale_id,
                            )?.name
                          }
                          )
                        </span>
                      </div>
                    ))}
                  {skills.filter((s) => s.category_id === category.id).length >
                    3 && (
                    <div className="text-sm text-gray-500">
                      +
                      {skills.filter((s) => s.category_id === category.id)
                        .length - 3}{" "}
                      more...
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCreateSkill(category.id)}
                  >
                    Add Skill
                  </Button>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditCategory(category)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteCategoryClick(category)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const SkillsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          All Skills ({skills.length})
        </h2>
        <Button onClick={() => handleCreateSkill()}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Skill
        </Button>
      </div>

      {skills.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No skills yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create skills to evaluate your players on specific abilities.
            </p>
            <div className="mt-6">
              <Button onClick={() => handleCreateSkill()}>
                Create First Skill
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {categories.map((category) => {
            const categorySkills = skills.filter(
              (skill) => skill.category_id === category.id,
            );
            if (categorySkills.length === 0) return null;

            return (
              <Card key={category.id}>
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <CardTitle>{category.name}</CardTitle>
                    <Badge variant="gray">{categorySkills.length} skills</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {categorySkills.map((skill) => {
                      const scale = ratingScales.find(
                        (s) => s.id === skill.rating_scale_id,
                      );
                      return (
                        <div
                          key={skill.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900">
                                {skill.name}
                              </h4>
                              {skill.is_required && (
                                <Badge variant="blue" className="text-xs">
                                  Required
                                </Badge>
                              )}
                              {skill.weight !== 1 && (
                                <Badge variant="yellow" className="text-xs">
                                  Weight: {skill.weight}x
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                              <span>Scale: {scale?.name}</span>
                              {scale?.scale_type === "numeric" && (
                                <span>
                                  {scale.min_value}-{scale.max_value}
                                  (±{scale.increment})
                                </span>
                              )}
                            </div>
                            {skill.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {skill.description}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSkill(skill)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteSkillClick(skill)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCreateSkill(category.id)}
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Add Skill to {category.name}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const RatingScalesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Rating Scales ({ratingScales.length})
        </h2>
        <Button onClick={handleCreateRatingScale}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Rating Scale
        </Button>
      </div>

      {ratingScales.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No rating scales yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create rating scales to define how skills are scored.
            </p>
            <div className="mt-6">
              <Button onClick={handleCreateRatingScale}>
                Create First Rating Scale
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ratingScales.map((scale) => (
            <Card key={scale.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{scale.name}</CardTitle>
                  <div className="flex items-center space-x-2">
                    <Badge
                      variant={
                        scale.scale_type === "numeric"
                          ? "blue"
                          : scale.scale_type === "pass_fail"
                            ? "green"
                            : "purple"
                      }
                    >
                      {scale.scale_type.replace("_", " ")}
                    </Badge>
                    <Badge variant="gray">
                      {
                        skills.filter((s) => s.rating_scale_id === scale.id)
                          .length
                      }{" "}
                      skills
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {scale.description && (
                    <p className="text-sm text-gray-600">{scale.description}</p>
                  )}

                  <div className="bg-gray-50 rounded-md p-3">
                    {scale.scale_type === "numeric" && (
                      <div className="text-sm">
                        <div className="font-medium">
                          Range: {scale.min_value} - {scale.max_value}
                        </div>
                        <div className="text-gray-600">
                          Increment: {scale.increment}
                        </div>
                        <div className="text-gray-600 mt-1">
                          Example values: {scale.min_value},{" "}
                          {scale.min_value + scale.increment},{" "}
                          {scale.min_value + scale.increment * 2}...
                        </div>
                      </div>
                    )}

                    {scale.scale_type === "pass_fail" && (
                      <div className="text-sm font-medium">
                        Options: Pass / Fail
                      </div>
                    )}

                    {scale.scale_type === "custom_options" && (
                      <div className="text-sm">
                        <div className="font-medium mb-2">Options:</div>
                        <div className="flex flex-wrap gap-1">
                          {scale.custom_options?.map((option, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {option}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {skills.filter((s) => s.rating_scale_id === scale.id).length >
                    0 && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">
                        Used by skills:
                      </div>
                      <div className="text-sm text-gray-600">
                        {skills
                          .filter((s) => s.rating_scale_id === scale.id)
                          .map((s) => s.name)
                          .join(", ")}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    {skills.filter((s) => s.rating_scale_id === scale.id)
                      .length === 0
                      ? "Not used by any skills"
                      : `Used by ${
                          skills.filter((s) => s.rating_scale_id === scale.id)
                            .length
                        } skill(s)`}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditRatingScale(scale)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteRatingScaleClick(scale)}
                      disabled={
                        skills.filter((s) => s.rating_scale_id === scale.id)
                          .length > 0
                      }
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Skills Management
          </h1>
          <p className="text-gray-600 mt-1">
            Define evaluation criteria and rating scales for your club
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setActiveTab("scales")}>
            <Cog6ToothIcon className="w-4 h-4 mr-2" />
            Rating Scales
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/portal/club/${clubId}/dashboard`)}
          >
            Back to Club
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <SwatchIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">
                  {categories.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <AcademicCapIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Skills</p>
                <p className="text-2xl font-bold text-gray-900">
                  {skills.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Rating Scales
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {ratingScales.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "categories", name: "Categories", icon: SwatchIcon },
            { id: "skills", name: "Skills", icon: AcademicCapIcon },
            { id: "scales", name: "Rating Scales", icon: ChartBarIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-slate-500 text-slate-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {getTabContent()}

      {/* Modals */}
      <Modal
        isOpen={showRatingScaleModal}
        onClose={() => {
          setShowRatingScaleModal(false);
          setEditingItem(null);
        }}
        title={editingItem ? "Edit Rating Scale" : "Create Rating Scale"}
        size="lg"
      >
        <RatingScaleForm
          clubId={clubId}
          editingScale={editingItem}
          onSuccess={() => {
            setShowRatingScaleModal(false);
            setEditingItem(null);
            loadAllData();
          }}
          onCancel={() => {
            setShowRatingScaleModal(false);
            setEditingItem(null);
          }}
        />
      </Modal>

      <Modal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingItem(null);
        }}
        title={editingItem ? "Edit Category" : "Create Category"}
      >
        <SkillCategoryForm
          clubId={clubId}
          editingCategory={editingItem}
          onSuccess={() => {
            setShowCategoryModal(false);
            setEditingItem(null);
            loadAllData();
          }}
          onCancel={() => {
            setShowCategoryModal(false);
            setEditingItem(null);
          }}
        />
      </Modal>

      <Modal
        isOpen={showSkillModal}
        onClose={() => {
          setShowSkillModal(false);
          setEditingItem(null);
        }}
        title={editingItem?.id ? "Edit Skill" : "Create Skill"}
        size="lg"
      >
        <SkillForm
          clubId={clubId}
          categories={categories}
          ratingScales={ratingScales}
          editingSkill={editingItem}
          onSuccess={() => {
            setShowSkillModal(false);
            setEditingItem(null);
            loadAllData();
          }}
          onCancel={() => {
            setShowSkillModal(false);
            setEditingItem(null);
          }}
        />
      </Modal>

      {/* Category Details Modal */}
      <Modal
        isOpen={showCategoryDetailsModal}
        onClose={() => {
          setShowCategoryDetailsModal(false);
          setSelectedCategory(null);
        }}
        title="Category Details"
        size="lg"
      >
        {selectedCategory && (
          <div className="space-y-6">
            {/* Category Header */}
            <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
              <div
                className="w-6 h-6 rounded-full"
                style={{ backgroundColor: selectedCategory.color }}
              />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedCategory.name}
                </h3>
                {selectedCategory.description && (
                  <p className="text-gray-600 mt-1">
                    {selectedCategory.description}
                  </p>
                )}
              </div>
            </div>

            {/* Skills List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">
                  Skills (
                  {
                    skills.filter((s) => s.category_id === selectedCategory.id)
                      .length
                  }
                  )
                </h4>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowCategoryDetailsModal(false);
                    handleCreateSkill(selectedCategory.id);
                  }}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Skill
                </Button>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {skills
                  .filter((skill) => skill.category_id === selectedCategory.id)
                  .map((skill) => {
                    const scale = ratingScales.find(
                      (s) => s.id === skill.rating_scale_id,
                    );
                    return (
                      <div
                        key={skill.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h5 className="font-medium text-gray-900">
                                {skill.name}
                              </h5>
                              {skill.is_required && (
                                <Badge variant="blue" className="text-xs">
                                  Required
                                </Badge>
                              )}
                              {skill.weight !== 1 && (
                                <Badge variant="yellow" className="text-xs">
                                  Weight: {skill.weight}x
                                </Badge>
                              )}
                            </div>

                            <div className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">Rating Scale:</span>{" "}
                              {scale?.name}
                              {scale?.scale_type === "numeric" && (
                                <span className="ml-2">
                                  ({scale.min_value}-{scale.max_value}, ±
                                  {scale.increment})
                                </span>
                              )}
                            </div>

                            {skill.description && (
                              <p className="text-sm text-gray-600 mb-2">
                                <span className="font-medium">
                                  Description:
                                </span>{" "}
                                {skill.description}
                              </p>
                            )}

                            {skill.evaluation_criteria && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">
                                  Evaluation Criteria:
                                </span>{" "}
                                {skill.evaluation_criteria}
                              </p>
                            )}
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setShowCategoryDetailsModal(false);
                                handleEditSkill(skill);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteSkillClick(skill)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {skills.filter((s) => s.category_id === selectedCategory.id)
                  .length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No skills in this category
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Add the first skill to this category.
                    </p>
                    <div className="mt-4">
                      <Button
                        onClick={() => {
                          setShowCategoryDetailsModal(false);
                          handleCreateSkill(selectedCategory.id);
                        }}
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add First Skill
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Created: {new Date().toLocaleDateString()}{" "}
                {/* You can add actual creation date if available */}
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCategoryDetailsModal(false);
                    handleEditCategory(selectedCategory);
                  }}
                >
                  Edit Category
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCategoryDetailsModal(false);
                    setSelectedCategory(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteContext({ type: "", item: null });
        }}
        onConfirm={handleDeleteConfirm}
        title={deleteContext.title}
        message={deleteContext.message}
        confirmText="Delete"
        loading={deleting}
      />
    </div>
  );
};

export default SkillsManagement;
